import { CancelStopDialog } from '@folio/funnels/Stop/CancelStopDialog';
import { stopDraftResumePath } from '@folio/funnels/Stop/resume-step';
import { useCancelStop } from '@folio/funnels/Stop/useCancelStop';
import { useStopDraft } from '@folio/funnels/Stop/useStopDraft';
import { stopRowAction } from '@folio/utils/stop-branch';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import type { BranchDto } from 'korint-sdk';
import { useFeatureFlag } from 'korint-sdk/react';
import { EllipsisVerticalIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Popover, PopoverContent } from '@folio/components';
import { PopoverAnchor } from '@radix-ui/react-popover';
import { formatZonedDateTime } from '../../lib/date-formatting';
import { Stack } from '@folio/components/ui/flex';

/** The banner's stop actions, laid out like an MTA row: forward inline, closing in the menu. */
export function StopLineActions({
  branch,
  stopFunnelBasePath,
}: {
  branch: BranchDto;
  stopFunnelBasePath?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { policy, policyOnMain, branches } = usePolicyOnDashboard();
  const flagEnabled = useFeatureFlag('ops_policy_stop_branch', policy.productId);
  const [openPopover, setOpenPopover] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const baseStop =
    policyOnMain.stop && policyOnMain.status !== 'POLICY_STOPPED' ? policyOnMain.stop : undefined;
  const action = stopRowAction({ branch, branches: branches?.data, baseStop });

  const { draft, discard, isDiscarding } = useStopDraft(policy.id, {
    enabled: action.kind === 'resume',
  });
  const { mayCancel, cancel, isCancelling } = useCancelStop();

  const resumeDraft =
    action.kind === 'resume' && draft && stopFunnelBasePath && flagEnabled ? draft : undefined;
  const discardableDraft = action.kind === 'resume' ? draft : null;
  const cancelNamespace =
    action.kind === 'cancel'
      ? action.state === 'awaitingValidation'
        ? 'stopAwaitingValidation'
        : 'scheduledStop'
      : undefined;
  const canCancel = !!cancelNamespace && mayCancel;

  if (!resumeDraft && !discardableDraft && !canCancel) {
    return null;
  }

  const stopDate = policyOnMain.stoppedAt ?? baseStop?.stopDate;
  const reason = baseStop
    ? t([
        `${policy.productId}.stopReasons.${baseStop.reason}`,
        `stopReasons.${baseStop.reason}`,
        baseStop.reason,
      ])
    : '';

  return (
    <>
      {resumeDraft && stopFunnelBasePath && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate(
              stopDraftResumePath({
                funnelBasePath: stopFunnelBasePath,
                policyId: policy.id,
                draft: resumeDraft,
              }),
            )
          }
        >
          {t(resumeDraft.refusedBranchId ? 'stopRefusal.resume' : 'stopDraft.resume')}
        </Button>
      )}

      {(discardableDraft || canCancel) && (
        <Popover open={openPopover} onOpenChange={setOpenPopover}>
          <PopoverAnchor>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOpenPopover(true)}
              aria-label={t('policyDashboardEvents.actions.showBranchActions')}
            >
              <EllipsisVerticalIcon aria-hidden="true" />
            </Button>
          </PopoverAnchor>
          <PopoverContent>
            <Stack align="start" gap={2}>
              {discardableDraft && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2Icon className="h-4 w-4" />}
                  className="w-full justify-start"
                  loading={isDiscarding}
                  onClick={() => discard(discardableDraft.branchId)}
                >
                  {t('stopDraft.discard')}
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<XIcon className="h-4 w-4" />}
                  className="w-full justify-start"
                  onClick={() => {
                    setOpenPopover(false);
                    setOpenDialog(true);
                  }}
                >
                  {t(`${cancelNamespace}.cancel`)}
                </Button>
              )}
            </Stack>
          </PopoverContent>
        </Popover>
      )}

      {/* A sibling of the popover: inside it, the dialog would unmount as the popover closes. */}
      {canCancel && (
        <CancelStopDialog
          open={openDialog}
          onOpenChange={setOpenDialog}
          title={t(`${cancelNamespace}.dialog.title`)}
          description={t(`${cancelNamespace}.dialog.description`, {
            date: stopDate ? formatZonedDateTime(new Date(stopDate), policyOnMain.timeZone) : '',
            reason,
          })}
          submitLabel={t(`${cancelNamespace}.dialog.submit`)}
          isPending={isCancelling}
          onSubmit={cancel}
        />
      )}
    </>
  );
}
